import {
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Stack,
  Text,
  VStack,
  useColorModeValue as mode,
  useDisclosure,
} from "@chakra-ui/react";
import {
  addDays,
  addMonths,
  addWeeks,
  differenceInDays,
  endOfMonth,
  endOfYear,
  getDay,
  previousMonday,
  startOfMonth,
  startOfYear,
  subDays,
  subMonths,
  subYears,
} from "date-fns";
import { enGB } from "date-fns/locale";
import numeral from "numeral";
import { formatAUDate, formatAUTime } from "../global-client";
import { useEffect, useState } from "react";
import "react-calendar/dist/Calendar.css";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useDispatch, useSelector } from "react-redux";
import { getTotals } from "../redux/actions/jobActions";
import "./dark-mode.css"; // Your dark mode styles
import { getInvoices } from "../redux/actions/invoiceActions";
import InvoiceRow from "./screenComponents/InvoiceRow.jsx";
import InvoiceModal from "./screenComponents/InvoiceModal.jsx";

const TotalsScreen = () => {
  const dispatch = useDispatch();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [showInvoices, setShowInvoices] = useState(false);
  const [invSelectedRow, setInvSelectedRow] = useState(null);
  const [invSelected, setInvSelected] = useState(null);
  const [dateType, setDateType] = useState("today");
  //try having both start and end date as one state to prevent multiple calls to network
  //const [dateRange, setDateRange] = useState({ start: new Date(), end: new Date() });
  const { loading, totals, ttotal } = useSelector((state) => state.job);
  const { loadingInv, invoices } = useSelector((state) => state.invoice);

  const onChange = (dates) => {
    const [start, end] = dates;
    //console.log("Selected dates: ", dates);
    setStartDate(start);
    setEndDate(end);

    //setDateRange({ start: new Date(start), end: new Date(end) });
  };

  registerLocale("en-GB", enGB);

  const selectInvRow = (rowIndex) => {
    setInvSelectedRow(rowIndex);
    setInvSelected(invoices[rowIndex]);
  };

  const monthsAdd = (months) => {
    const sDate = addMonths(startDate, months);
    if (sDate) {
      setStartDate(new Date(sDate));
    }

    const eDate = addMonths(endDate, months);
    if (eDate) {
      setEndDate(new Date(eDate));
    }
  };

  const weeksAdd = (weeks) => {
    const sDate = addWeeks(startDate, weeks);
    if (sDate) {
      setStartDate(new Date(sDate));
    }

    const eDate = addWeeks(endDate, weeks);
    if (eDate) {
      setEndDate(new Date(eDate));
    }
  };

  const setCustomRange = (range) => {
    const today = new Date();
    const tomorrow = addDays(today, 1);
    let start, end;

    switch (range) {
      case "Today":
        start = today;
        end = today;
        setDateType("today");
        break;
      case "Yesterday":
        if (getDay(today) === 1) {
          start = subDays(today, 2);
        } else {
          start = subDays(today, 1);
        }
        end = start;
        setDateType("yesterday");
        break;
      case "Last 7 Days":
        start = subDays(today, 6);
        end = today;
        setDateType("last7Days");
        break;
      case "Last Week":
        start = previousMonday(addWeeks(tomorrow, -1));
        end = addDays(start, 6);
        setDateType("lastWeek");
        break;
      case "This Week":
        start = previousMonday(tomorrow);
        end = addDays(start, 6);
        setDateType("thisWeek");
        break;
      case "This Month":
        start = startOfMonth(today);
        end = endOfMonth(today);
        setDateType("thisMonth");
        break;
      case "Last Month":
        start = startOfMonth(subMonths(today, 1));
        end = endOfMonth(subMonths(today, 1));
        setDateType("lastMonth");
        break;
      case "Last 3 Month":
        start = subMonths(today, 3);
        end = today;
        setDateType("lastTriMonth");
        break;
      case "This Year":
        start = startOfYear(today);
        end = endOfYear(today);
        setDateType("thisYear");
        break;
      case "Last Year":
        start = startOfYear(subYears(today, 1));
        end = endOfYear(subYears(today, 1));
        setDateType("lastYear");
        break;
      case "LifeTime":
        start = new Date(2015, 0, 1);
        end = today;
        setDateType("overview");
        break;
      default:
        break;
    }
    setStartDate(start);
    setEndDate(end);
    //setDateRange({ start, end });
  };

  useEffect(() => {
    dispatch(getTotals(new Date(startDate), new Date(endDate)));
    dispatch(getInvoices(startDate, endDate));
    //console.log("Dates updated: ", dateRange.start, dateRange.end);
    //dispatch(getTotals(new Date(dateRange.start), new Date(dateRange.end)));
    document.title = numeral(ttotal ? ttotal : 0).format("$0,0.00");

    // const interval = setInterval(() => {
    //   if (document.visibilityState === "visible") {
    //     dispatch(getTotals(new Date(startDate), new Date(endDate)));
    //   }
    // }, 15000); //set your time here. repeat every 15 seconds

    return () => {
      //clearInterval(interval);
      document.title = "GF Job Manager";
    };
  }, [dispatch, startDate, endDate, dateType, ttotal]);

  // if (mode("0", "1") === "1") {
  //   import("./dark-mode.css");
  // } else {
  //   import("./light-mode.css");
  // }

  return (
    <Stack direction='column' align='center' justify='flex-start' pt={2}>
      <HStack pt='4' mb='4' minH='40px'>
        <Button variant='link' onClick={() => window.history.back()} mr='4'>
          &larr; Back
        </Button>
        <Button variant='link' onClick={() => dispatch(getTotals(new Date(startDate), new Date(endDate)))} mr='4'>
          &#x21bb; Refresh
        </Button>
        {loading ? (
          <Box textAlign='center' ml={1} minW='30px'>
            {/* <Spinner size='xs' /> */}
            <Text size='xs' colorScheme='cyan'>
              {/* &#9851; */}
              &#10560;
            </Text>
          </Box>
        ) : (
          <Box ml={1} minW='30px'></Box>
        )}
      </HStack>

      <Stack
        direction={{ base: "column", md: "row" }}
        spacing={4}
        align={{ base: "center", md: "start" }}
        justify='start'
      >
        <Stack direction='row' align='start' justify='center'>
          <VStack textAlign='center' align='start' mt={-2} borderWidth='1px' p={1} spacing={2} minH='278px'>
            <Button
              size='sm'
              w='100%'
              h='26px'
              fontWeight='normal'
              onClick={() => {
                setCustomRange("Today");
              }}
            >
              Today
            </Button>
            <Button
              size='sm'
              w='100%'
              h='26px'
              fontWeight='normal'
              onClick={() => {
                setCustomRange("Yesterday");
              }}
            >
              Yesterday
            </Button>
            <Button
              size='sm'
              w='100%'
              h='26px'
              fontWeight='normal'
              onClick={() => {
                setCustomRange("This Week");
              }}
            >
              This Week
            </Button>
            <Button
              size='sm'
              w='100%'
              h='26px'
              fontWeight='normal'
              onClick={() => {
                setCustomRange("Last Week");
              }}
            >
              Last Week
            </Button>
            <Button
              size='sm'
              w='100%'
              h='26px'
              fontWeight='normal'
              onClick={() => {
                setCustomRange("This Month");
              }}
            >
              This Month
            </Button>
            <Button
              size='sm'
              w='100%'
              h='26px'
              fontWeight='normal'
              onClick={() => {
                setCustomRange("Last Month");
              }}
            >
              Last Month
            </Button>
            <Button
              size='sm'
              w='100%'
              h='26px'
              fontWeight='normal'
              onClick={() => {
                setCustomRange("This Year");
              }}
            >
              This Year
            </Button>
            <Button
              size='sm'
              w='100%'
              h='26px'
              fontWeight='normal'
              onClick={() => {
                setCustomRange("Last Year");
              }}
            >
              Last Year
            </Button>
          </VStack>
          <Stack direction='column' minH='280px'>
            <Box width='240px' display='flex' justifyContent='space-between' spacing={2} mt={-2} borderWidth={1}>
              <Button size='sm' fontSize='xs' onClick={() => monthsAdd(-12)} variant='ghost'>
                -Y
              </Button>
              <Button size='sm' fontSize='xs' onClick={() => monthsAdd(-1)} variant='ghost'>
                -M
              </Button>
              <Button size='sm' fontSize='xs' onClick={() => weeksAdd(-1)} variant='ghost'>
                -W
              </Button>
              {/* <Button
                                colorScheme='cyan'
                                fontSize='sm'
                                size='sm'
                                //variant='ghost'
                                onClick={() => {
                                    setStartDate(new Date());
                                    setEndDate(new Date());
                                }}
                            >
                                Today
                            </Button> */}
              <Button size='sm' fontSize='xs' onClick={() => weeksAdd(1)} variant='ghost'>
                +W
              </Button>
              <Button size='sm' fontSize='xs' onClick={() => monthsAdd(1)} variant='ghost'>
                +M
              </Button>
              <Button size='sm' fontSize='xs' onClick={() => monthsAdd(12)} variant='ghost'>
                +Y
              </Button>
            </Box>
            <Box className={mode("", "dark-mode")}>
              <DatePicker
                key={startDate && endDate && startDate.toString() + endDate.toString()}
                selected={startDate}
                onChange={onChange}
                startDate={startDate}
                endDate={endDate}
                selectsRange
                inline
                locale='en-GB'
              />
            </Box>
          </Stack>
        </Stack>
        <Flex
          alignSelf='start'
          height='250px'
          flexDirection='column'
          justify='flex-start'
          align='center'
          mt={{ base: "0", md: "-8px" }}
          ml={{ base: "0", md: "-8px" }}
        >
          <Flex
            textAlign='start'
            alignContent='start'
            flexDirection='column'
            borderWidth={1}
            width={{ base: "350px", md: "250px" }}
          >
            <HStack justifyContent='start' fontSize={"sm"}>
              <Box width={45} ml='4'>
                <Text>From: </Text>
              </Box>
              <Box>
                <Text fontWeight='bold'>{startDate ? formatAUDate(startDate, { weekday: false }) : "N/A"}</Text>
              </Box>
            </HStack>
            <HStack justifyContent='start' fontSize={"sm"}>
              <Box width={45} ml='4'>
                <Text>To: </Text>
              </Box>
              <Box>
                <Text fontWeight='bold'>{endDate ? formatAUDate(endDate, { weekday: false }) : "N/A"}</Text>
              </Box>
            </HStack>
          </Flex>
          <Flex
            alignSelf='flex-start'
            justifyItems='flex-start'
            flexDirection='column'
            p={4}
            borderWidth={1}
            borderRadius='md'
            width={{ base: "350px", md: "250px" }}
            minH='235px'
          >
            <Flex justifyContent='space-between' mb={2} fontWeight='bold'>
              <Text>Total: </Text>
              <Text>{numeral(totals && totals[0] ? totals[0].TTOTAL : 0).format("$0,0.00")}</Text>
            </Flex>
            <Divider mb={1} />
            <Flex justifyContent='space-between' mb={2}>
              <Text>Cash: </Text>
              <Text>{numeral(totals && totals[0] ? totals[0].CASH : 0).format("$0,0.00")}</Text>
            </Flex>
            <Flex justifyContent='space-between' mb={2}>
              <Text>Cards: </Text>
              <Text>{numeral(totals && totals[0] ? totals[0].CARDS : 0).format("$0,0.00")}</Text>
            </Flex>
            <Flex justifyContent='space-between' mb={2}>
              <Text>Bank transfer: </Text>
              <Text>{numeral(totals && totals[0] ? totals[0].BANK : 0).format("$0,0.00")}</Text>
            </Flex>
            <Flex justifyContent='space-between' mb={2}>
              <Text>MOTO: </Text>
              <Text>{numeral(totals && totals[0] ? totals[0].MOTO : 0).format("$0,0.00")}</Text>
            </Flex>
            <Divider mb={4} />
            <Flex justifyContent='center' mb={2}>
              <Button
                variant='link'
                size='sm'
                p={0}
                m={0}
                fontWeight='normal'
                onClick={() => setShowInvoices(!showInvoices)}
              >
                {showInvoices ? "Hide Receipts" : "Show Receipts"}
              </Button>
            </Flex>
          </Flex>
        </Flex>
      </Stack>
      <Box>
        <InvoiceModal isOpen={isOpen} onClose={onClose} invSelectedRow={invSelectedRow} inv={invSelected} />
      </Box>
      {showInvoices && (
        <Stack>
          <Box height='20px' minW={{ base: "350px", md: "615px" }} mt={{ base: "8", md: "0" }}>
            <Text fontSize='xs' textAlign='center' p={2}>
              Receipts for {startDate ? formatAUDate(startDate, { weekday: false }) : "N/A"}{" "}
              {endDate &&
                differenceInDays(endDate, startDate) > 0 &&
                `to ${endDate ? formatAUDate(endDate, { weekday: false }) : "N/A"}`}
              {invoices && invoices.length > 0 && ` - ${invoices.length} receipt(s) found.`}
            </Text>
            {invoices && invoices.length === 0 && !loadingInv && (
              <Text fontSize='xs' textAlign='center' p={1}>
                No receipts found for this date.
              </Text>
            )}
            {loadingInv ? (
              <Text fontSize='xs' textAlign='center' p={1}>
                Loading receipts...
              </Text>
            ) : (
              invoices &&
              invoices.length > 0 && (
                <Flex
                  direction='column'
                  borderWidth='1px'
                  borderRadius='lg'
                  p='4'
                  mb='2'
                  fontFamily='sans-serif'
                  fontSize='sm'
                >
                  <InvoiceRow
                    key={"header"}
                    index={-1}
                    date='Date'
                    time='Time'
                    number='Invoice No'
                    total='Total'
                    header={true}
                  />
                  {invoices.map((inv, index) => (
                    <InvoiceRow
                      key={inv.INVOICE_NUM}
                      index={index}
                      header={false}
                      date={formatAUDate(inv.INVOICE_DATE, { weekday: false })}
                      time={formatAUTime(inv.INVOICE_DATE)}
                      number={inv.INVOICE_NUM}
                      total={numeral(inv.TOTAL).format("$0,0.00")}
                      onSelect={(row) => {
                        selectInvRow(row);
                      }}
                      onDouble={onOpen}
                      selected={invSelectedRow === index}
                      invoice={inv}
                    />
                  ))}
                </Flex>
              )
            )}
          </Box>
        </Stack>
      )}
    </Stack>
  );
};

export default TotalsScreen;
